/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import classnames from 'classnames';
import {omitBy, xorWith} from 'lodash';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import get from 'lodash/get';
import has from 'lodash/has';
import {translate} from '@ion/core/src/locale/locale';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import styles from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {deviceSCISelector} from '../../store/selectors/devices';
import {SCIAdvanceTab} from './SCIAdvanceTab';
import {SCICustomTab} from './SCICustomTab';
import {SCIReportsTab} from './SCIReportsTab';
import {SCISensorsTab} from './SCISensorsTab';
import {getDefaultFieldsObj} from './SCISettingFieldMapping';
import {
  convertLoggingPeriodInterval,
  defaultReceiveInterval,
  defaultReceiveTime,
  getAddedNetwork,
  sensorLoggingPeriodList
} from './SCISettingsUtils';
import {SCIWifiTab} from './SCIWifiTab';
import {clearSCI, updateSCISetting, viewAdminDevice} from './state';

export interface SCIDeviceDataType {
  tabIndex: Number;
  SCIDeviceData: Device;
  noUpdate: boolean;
  serialNumber: string | Number;
  hasSciPermission: boolean;
};

export interface PaperWrapperProps {
  classes?: any;
  children?: ReactNode;
}

export const styleOverrides = (theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display : 'flex'
  },
  leftContainer : {
    flex        : 1,
    marginTop   : '21px',
    marginRight : '10px',
    padding     : '20px',
    width       : '30%'
  },
  rightContainer : {
    width        : '75%',
    borderRadius : '5px',
    marginTop    : '21px',
    padding      : '0px 10px',
    height       : '430px',
    overflowY    : 'auto'
  },
  paraStyle : {
    margin : '10px 4px 20px 6px'
  },
  idReportColor : {
    borderBottom : `1px solid ${dlsColor('aaBlue')}`,
    color        : dlsColor('black')
  },
  headerItem : {
    ...dlsAlt('typography', 'label1'),
    fontWeight    : '700',
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px'
  },
  listText : {
    display : 'block'
  },
  divPara : {
    whiteSpace : 'pre-wrap'
  },
  paperStyle : {
    display         : 'inline-block',
    boxShadow       : 'none',
    backgroundColor : 'transparent !important'
  },
  noData : {
    width          : '100%',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    height         : '400px',
    boxShadow      : 'none'
  },
  idReportPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    width     : '83%',
    boxShadow : 'none',
    display   : 'flex'
  },
  idReportList : {
    listStyleType : 'none',
    lineHeight    : '25px',
    textTransform : 'uppercase'
  },
  sensorFiels : {
    marginTop : '20px',
    overflow  : 'auto',
    width     : '75%',
    boxShadow : 'none',
    display   : 'flex'
  },
  serviceStyle : {
    marginBottom : '30px'
  },
  listStyle : {
    listStyleType : 'square'
  },
  reportsFields : {
    width : '33%'
  },
  addBtn : {
    marginTop : '24px'
  },
  sensorSubFormWidth : {
    width : '153%'
  },
  pendingStatus : {
    padding : '21px 0 0 5px'
  },
  sensorPendingStatus : {
    padding : '31px 0 0 5px',
    width   : '24%'
  },
  reportPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    boxShadow : 'none',
    display   : 'flex'
  },
  reportsPendingStatus : {
    padding : '30px 0 0 5px',
    width   : '20%'
  },
  advancePendingStatus : {
    padding : '23px 0 0 5px'
  },
  sensorFields : {
    paddinfBottom : '10px'
  },
  sensorFormFields : {
    width           : '75%',
    backgroundColor : 'white'
  },
  sensorSubFormSytle : {
    width           : '80%',
    backgroundColor : 'white'
  },
  gridStyle : {
    paddingTop : '10px',
    width      : '94%'
  },
  advanceTabPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    width     : '100%',
    boxShadow : 'none',
    display   : 'flex'
  },
  itemStyle : {
    ...dlsAlt('typography', 'label1'),
    color        : dlsColor('grey2'),
    marginBottom : '20px'
  },
  btnStyle : {
    display        : 'flex',
    justifyContent : 'end'
  },
  customTabPaper : {
    marginTop : '20px',
    overflow  : 'auto',
    width     : '91%',
    boxShadow : 'none',
    display   : 'flex'
  },
  noDataStyle : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    height         : '350px',
    width          : '600px'
  },
  sensorMaxTempStyle : {
    padding  : '13px 0 0 563px',
    width    : '24%',
    position : 'absolute'
  },
  syncSensorLogStyle : {
    padding : '0px 0 0 5px',
    width   : '24%'
  }
});

export const NoData = ({classes}: PaperWrapperProps) => {
  return (
    <Paper className={classes.noData}>
      {translate('admin-devices:view.manufacturing.noDataAvailable')}
    </Paper>
  );
};

export const PaperWrapper = ({children, classes}: PaperWrapperProps) => {
  return (
    <Paper className={classes?.idReportPaper}>
      <MenuList>
        <MenuItem >
          {children}
        </MenuItem>
      </MenuList>
    </Paper>
  );
};

/**
 * SCISettingsTab component that displays the Device SCI setting
 * data for the selected Device Details tab.
 */
export const SCISettingsTab = ({
  tabIndex = 0,
  SCIDeviceData,
  noUpdate,
  serialNumber,
  hasSciPermission
}: SCIDeviceDataType) => {
  const editorStyles  = useExtendStyles(styles, styleOverrides);
  const classes       = editorStyles();
  const dispatch      = useDispatch();
  const isFirstRun    = useRef(true);

  const [sciTabValue, setSciTabValue]                           = useState<number>(0);
  const [sciPendingSettingData, setSciPendingSettingData]       = useState<GenericObject>({});
  const [sciSettingsInitialFields, setSciSettingsInitialFields] = useState<GenericObject>({});
  const [initialReportAndLog, setInitialReportAndLog]           = useState<boolean>(true);
  const [initialAdvance, setInitialAdvance]                     = useState<boolean>(true);
  const [changedSettingsFields, setChangedSettingsFields]       = useState<GenericObject>({});
  const [tagDetectionModeVal, setTagDetectionModeVal]           = useState<string>('');
  const [newWifiNetwork, setNewWifiNetwork]                     = useState({ssid: '', pass: ''});
  const [customTabFields, setCustomTabFields]                   = useState<GenericObject[]>([]);
  const [reportTabFields, setReportTabFields]                   = useState<GenericObject[]>([]);
  const [sensorsTabFields, setSensorsTabFields]                 = useState<GenericObject[]>([]);
  const [advanceTabFields, setAdvanceTabFields]                 = useState<GenericObject[]>([]);
  const [isRemoveWifi, setIsRemoveWifi]                         = useState<string>(null);

  const deviceModel = SCIDeviceData?.type?.title;
  const SCISettingsData = SCIDeviceData?.sciDeviceSettings;
  const SCISettingsDataStringified = JSON.stringify(SCISettingsData);

  const [, , , , wifi] = getDefaultFieldsObj(sciSettingsInitialFields, deviceModel);
  const [wifiNetworks, setWifiNetworks] = useState(wifi);

  const {
    result: sciResult,
    loading: isSciLoading
  } = useSelector(deviceSCISelector);

  const SCISettingsArr = useMemo(() => {
    const {controls, reportAndLog, sensors, wifi, advance} = translate('admin-devices:view.sciSettings', {
      returnObjects : true
    });
    return [controls, reportAndLog, sensors, wifi, advance];
  }, []);

  useEffect(() => {
    const initialSettingsFields = SCISettingsData && has(SCISettingsData, 'settings') && !isEmpty(SCISettingsData.settings) && SCISettingsData.settings;
    const initialPendingFields = SCISettingsData && has(SCISettingsData, 'pendingSettings') && !isEmpty(SCISettingsData.pendingSettings) && SCISettingsData.pendingSettings;

    const zMicroRxPeriodValue = !isEmpty(initialSettingsFields) && has(initialSettingsFields, 'zMicroRxPeriod') && initialSettingsFields.zMicroRxPeriod;
    const defaultSelected = parseInt(zMicroRxPeriodValue) === 0 ? translate('admin-devices:view.sciSettings.advanceTagOptions.onlyWhenReporting')
      : parseInt(zMicroRxPeriodValue) === 1 ? translate('admin-devices:view.sciSettings.advanceTagOptions.alwaysOn')
        : translate('admin-devices:view.sciSettings.advanceTagOptions.timed');
    if (initialPendingFields?.wifiNetworks) {
      const wifi = {
        ...wifiNetworks,
        wifiNetworks : {
          ...wifiNetworks?.wifiNetworks,
          networks : [...wifiNetworks?.wifiNetworks?.networks, ...initialPendingFields?.wifiNetworks?.networks]
        }
      };
      setWifiNetworks(wifi);
    }
    const [customInitialFields,
      reportAndLogsInitialFields,
      sensorsInitialFields,
      advanceInitialFields] = getDefaultFieldsObj(initialSettingsFields, deviceModel);

    setCustomTabFields([...customInitialFields]);
    setReportTabFields([...reportAndLogsInitialFields]);
    setSensorsTabFields([...sensorsInitialFields]);
    setAdvanceTabFields([...advanceInitialFields]);
    setSciPendingSettingData({...initialPendingFields});
    setSciSettingsInitialFields({...initialSettingsFields});
    setTagDetectionModeVal(defaultSelected);
  }, [SCISettingsDataStringified]);

  useEffect(() => {
    const customAndSensorsStates = [customTabFields, sensorsTabFields];
    const reportAndAdvanceStates = [reportTabFields, advanceTabFields];
    let extractedFields = {};
    let isWifiChange = false;
    const networksInitialFields = sciSettingsInitialFields?.wifiNetworks?.networks;
    const changedWifiNetworks = wifiNetworks?.wifiNetworks?.networks;
    customAndSensorsStates.map(tab => {
      tab.forEach(function(item) {
        for (const [key, value] of Object.entries(item)) {
          if (key !== 'label') {
            extractedFields[key] = value;
          }
        }
      });
    });
    reportAndAdvanceStates.map((tab, indx) => {
      tab.map((itemArr) => {
        itemArr.forEach(function(item) {
          for (const [key, value] of Object.entries(item)) {
            if (key !== 'label') {
              return (extractedFields[key] = value);
            }
          }
        });
      });
    });

    if (isFirstRun.current) {
      isFirstRun.current = false;
    } else {
      const checkWifiChange = isArrayEqual(networksInitialFields, changedWifiNetworks);
      if (checkWifiChange === translate('admin-devices:view.sciSettings.wifiChangedStatus')) {
        isWifiChange = true;
      }
    }

    extractedFields = omitBy(extractedFields, isUndefined);
    const changedSettingsFields: any = updatedDiff(sciSettingsInitialFields, extractedFields);
    if (isWifiChange) {
      changedSettingsFields.wifiNetworks = getAddedNetwork(networksInitialFields, changedWifiNetworks);
    }
    setChangedSettingsFields(changedSettingsFields);
  }, [customTabFields, reportTabFields, sensorsTabFields, advanceTabFields, wifiNetworks]);

  useEffect(() => {
    if (isRemoveWifi) {
      const wifiNetworksData = wifiNetworks?.wifiNetworks?.networks;
      const copyWifiNetworksData = wifiNetworksData;
      for (let i = 0; i < copyWifiNetworksData.length; i++) {
        if (copyWifiNetworksData[i].ssid === isRemoveWifi) {
          copyWifiNetworksData.splice(i, 1);
          break;
        }
      }
      setWifiNetworks({
        ...wifiNetworks,
        wifiNetworks : {...wifiNetworks.wifiNetworks, networks: [...copyWifiNetworksData]}
      });
      setIsRemoveWifi(null);
    }
  }, [isRemoveWifi]);

  useEffect(() => {
    if (hasSciPermission && sciResult) {
      dispatch(viewAdminDevice(null, false));
      dispatch(clearSCI());
    }
  }, [sciResult]);

  const handleNetworkChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    setNewWifiNetwork({...newWifiNetwork, [e.target.name]: e.target.value});
  }, [newWifiNetwork]);

  const getSCIProperties = useCallback((e) => {
    const idReportText = e.target.innerText;
    const convertedSCIArr = SCISettingsArr.map((item) => item.toUpperCase());
    const index = convertedSCIArr.indexOf(idReportText);
    setSciTabValue(index);
  }, []);

  function pendingSensorValueCheck(sensorIndx: number, field: GenericObject) {
    if (field.label === translate('admin-devices:view.sciSettings.lightLevelAlert')) {
      if (has(sciPendingSettingData, 'lightLevelAlert') || has(sciPendingSettingData, 'minLightLevel') || has(sciPendingSettingData, 'maxLightLevel')) {
        return (
          (<div>
            {'(Pending '}{`${has(sciPendingSettingData, 'lightLevelAlert') ? (get(sciPendingSettingData, 'lightLevelAlert') === true ? 'ON' : 'OFF') : ''} ${has(sciPendingSettingData, 'minLightLevel') ? `, MIN: ${get(sciPendingSettingData, 'minLightLevel')}` : ''} ${has(sciPendingSettingData, 'maxLightLevel') ? `, MAX: ${get(sciPendingSettingData, 'maxLightLevel')}` : ''}`.trim().replace(/^,|,$/g, '')}{')'}
          </div>)
        );
      }
    }
    if (field.label === translate('admin-devices:view.sciSettings.temperatureAlert')) {
      if (has(sciPendingSettingData, 'temperatureAlert') || has(sciPendingSettingData, 'minTemperature') || has(sciPendingSettingData, 'maxTemperature')) {
        return (
          (<div>
            {'(Pending '}{`${has(sciPendingSettingData, 'temperatureAlert') ? (get(sciPendingSettingData, 'temperatureAlert') === true ? 'ON' : 'OFF') : ''} ${has(sciPendingSettingData, 'minTemperature') ? `, MIN: ${get(sciPendingSettingData, 'minTemperature')}` : ''} ${has(sciPendingSettingData, 'maxTemperature') ? `, MAX: ${get(sciPendingSettingData, 'maxTemperature')}` : ''}`.trim().replace(/^,|,$/g, '')}{')'}
          </div>)
        );
      }
    }
    if (field.label === translate('admin-devices:view.sciSettings.impactAlert')) {
      if (has(sciPendingSettingData, 'impactAlert') || has(sciPendingSettingData, 'maxImpact')) {
        return (
          (<div>
            {'(Pending '}{`${has(sciPendingSettingData, 'impactAlert') ? (get(sciPendingSettingData, 'impactAlert') === true ? 'ON' : 'OFF') : ''} ${has(sciPendingSettingData, 'maxImpact') ? `, MAX: ${get(sciPendingSettingData, 'maxImpact')}` : ''}`.trim().replace(/^,|,$/g, '')}{')'}
          </div>)
        );
      }
    }
    if (field.label === translate('admin-devices:view.sciSettings.tipAlert')) {
      if (has(sciPendingSettingData, 'tipAlert') || has(sciPendingSettingData, 'maxTip')) {
        return (
          (<div>
            {'(Pending '}{`${has(sciPendingSettingData, 'tipAlert') ? (get(sciPendingSettingData, 'tipAlert') === true ? 'ON' : 'OFF') : ''} ${has(sciPendingSettingData, 'maxTip') ? `, MAX: ${get(sciPendingSettingData, 'maxTip')}` : ''}`.trim().replace(/^,|,$/g, '')}{')'}
          </div>)
        );
      }
    }
    if (field.label === translate('admin-devices:view.sciSettings.temperatureLogging')) {
      if (has(sciPendingSettingData, 'temperatureLogging') || has(sciPendingSettingData, 'sensorLoggingPeriod') || has(sciPendingSettingData, 'syncSensorLog') || has(sciPendingSettingData, 'syncSensorLogTempLimit')) {
        return (
          (<div>
            {'(Pending '}{`${has(sciPendingSettingData, 'temperatureLogging') ? (get(sciPendingSettingData, 'temperatureLogging') === true ? 'ON' : 'OFF') : ''}${has(sciPendingSettingData, 'sensorLoggingPeriod') ? `, Sensor Log Period: ${get(sciPendingSettingData, 'sensorLoggingPeriod')}` : ''} ${has(sciPendingSettingData, 'syncSensorLog') ? `, Sync Sensor Log: ${get(sciPendingSettingData, 'syncSensorLog') ? 'ON' : 'OFF'}` : ''} ${has(sciPendingSettingData, 'syncSensorLogTempLimit') ? `, Sync Sensor Log Temp: ${get(sciPendingSettingData, 'syncSensorLogTempLimit')} ${has(sciPendingSettingData, 'sensorEventMode') ? `, Sensor Event Mode: ${sensorLoggingPeriodList[get(sciPendingSettingData, 'sensorEventMode')]}` : ''}` : ''}`.trim().replace(/^,|,$/g, '')}{')'}
          </div>)
        );
      }
    }
  };

  const updatedDiff = (arr1, arr2) => {
    const diff = Object.keys(arr2).reduce((diff, key) => {
      if (arr1[key] === arr2[key]) return diff;
      return {
        ...diff,
        [key] : arr2[key]
      };
    }, {});
    return diff;
  };

  const isArrayEqual = (arr1, arr2) => {
    const check = isEmpty(xorWith(arr1, arr2, isEqual));
    if (check) {
      return translate('admin-devices:view.sciSettings.wifiNotChangedStatus');
    }
    return translate('admin-devices:view.sciSettings.wifiChangedStatus');
  };

  function handleCustomSwitchChange(e, key) {
    const initialControlData = customTabFields;
    initialControlData.find(switchItem => switchItem.label === e.target.name)[key] = e.target.checked;
    setCustomTabFields([...initialControlData]);
  };

  function handleSensorsFieldChange(e, label, key) {
    let initialControlData = sensorsTabFields;
    let convertedValue = e;
    if (typeof e === 'string') {
      convertedValue = convertLoggingPeriodInterval(e);
    } else if (typeof e === 'object') {
      convertedValue = e?.target?.value;
    }
    // const key = Object.keys(field)[1];
    const changedArray = initialControlData.map(field =>
      field.label === label
        ? {...field, [key]: convertedValue}
        : field
    );
    initialControlData = changedArray;
    setSensorsTabFields([...initialControlData]);
  };

  const getIsActive = function(value): boolean {
    return value;
  };

  const extendedClasses = useMemo(() => (
    classnames(classes.itemStyle, classes.idReportColor)
  ), [classes]);

  const getClass = (value: number, index: number, classes) => {
    return value === index ? extendedClasses : classes.itemStyle;
  };

  // Advnace tab Handler
  function handleAdvanceFieldChange(e, field, active) {
    const initialAdvanceData = advanceTabFields;
    const activeAdvanceSubTab = advanceTabFields[active];
    let convertedValue = e;
    const comparatorLabel = field.label;
    if (typeof e === 'string') {
      convertedValue = convertLoggingPeriodInterval(e);
    } else if (typeof e === 'object') {
      convertedValue = e?.target?.value;
    }
    const key = Object.keys(field)[1];
    const changedArray = activeAdvanceSubTab.map(field =>
      field.label === comparatorLabel
        ? {...field, [key]: convertedValue}
        : field
    );
    initialAdvanceData[active] = changedArray;
    setAdvanceTabFields([...initialAdvanceData]);
    setInitialAdvance(false);
  };

  function handleTagDetectionMode(e) {
    setTagDetectionModeVal(e.target.value);

    const initialAdvanceData = advanceTabFields;
    const activeAdvanceSubTab = advanceTabFields[1];
    let changedArray= [];
    changedArray = activeAdvanceSubTab.map(field => {
      if (field.label === 'Receive Interval (s)') {
        return {...field, zMicroRxPeriod: defaultReceiveInterval[e.target.value]};
      }
      if (field.label === 'Receive Time (ms)') {
        return {...field, zMicroRxTime: defaultReceiveTime[e.target.value]};
      }
      return field;
    });
    initialAdvanceData[1] = changedArray;
    setAdvanceTabFields([...initialAdvanceData]);
  };

  function handleReportAndLogs(e, field, active) {
    const initialReportState = reportTabFields;
    const activeReportSubTab = reportTabFields[active];
    let convertedReportingVal = e;
    const comparatorLabel = field.label;
    if (typeof e === 'string') {
      convertedReportingVal = convertLoggingPeriodInterval(e);
    } else if (typeof e === 'object') {
      convertedReportingVal = e?.target?.value;
    }
    const key = Object.keys(field)[1];
    const changedArray = activeReportSubTab.map(field =>
      field.label === comparatorLabel
        ? {...field, [key]: convertedReportingVal}
        : field
    );
    initialReportState[active] = changedArray;
    setReportTabFields([...initialReportState]);
    setInitialReportAndLog(false);
  };

  function onAddWifiNetwork() {
    if (newWifiNetwork.pass === '' || newWifiNetwork.ssid === '') {
      return;
    }
    const wifiNetworksData = wifiNetworks?.wifiNetworks?.networks;
    const networksArray = [...wifiNetworksData];
    networksArray.push(newWifiNetwork);
    setWifiNetworks({...wifiNetworks, wifiNetworks: {...wifiNetworks.wifiNetworks, networks: [...networksArray]}});
    setNewWifiNetwork({...newWifiNetwork, ssid: '', pass: ''});
  };

  function onRemoveWifiNetwork(removeRow: string) {
    if (noUpdate) {
      return;
    }
    setIsRemoveWifi(removeRow);
  };

  const renderAdvance = () => {
    let tagDetectionObj;
    let isTagDetectionOn = false;
    const controlsTabState = customTabFields;
    const model = SCISettingsData?.model?.toString();
    if (Array.isArray(controlsTabState) && controlsTabState.length > 0) {
      tagDetectionObj = controlsTabState.filter((item) => item.zMicroDetection);
      if (isUndefined(tagDetectionObj)) {
        isTagDetectionOn = true;
      } else {
        isTagDetectionOn = tagDetectionObj.length > 0 && has(tagDetectionObj[0], 'zMicroDetection') && tagDetectionObj[0].zMicroDetection;
      }
    }
    if (deviceModel === 'STAG09XT') {
      isTagDetectionOn = true;
    }
    return (
      <SCIAdvanceTab deviceModel={model}
        advanceTabFields={advanceTabFields}
        classes={classes}
        handleCustomSwitchChange={handleCustomSwitchChange}
        handleAdvanceFieldChange={handleAdvanceFieldChange}
        noUpdate={noUpdate}
        sciPendingSettingData={sciPendingSettingData}
        isTagDetectionOn={isTagDetectionOn}
        initialAdvance={initialAdvance}
        handleTagDetectionMode={handleTagDetectionMode}/>
    );
  };

  const onSubmit = useCallback((changedSettingsFields) => {
    dispatch(updateSCISetting(changedSettingsFields) as any);
  }, []);

  const onSCISubmit = useCallback(() => {
    const sciPayload = {
      deviceCommandReq : {
        deviceIdentifiers : [
          {
            identifier : serialNumber,
            type       : 'SERIAL_NUMBER'
          }
        ],
        commands : [
          {
            commandType : 'SciDeviceSettings',
            parameters  : changedSettingsFields
          }
        ]
      }
    };
    onSubmit(sciPayload);
  }, [changedSettingsFields, serialNumber]);

  return (
    <div id={`detailsTab_${tabIndex}`}>
      <div className={classes.flexStyle}>
        <div className={classes.leftContainer}>
          <Paper className={classes.paperStyle}>
            <MenuList>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 0, classes)}>
                {SCISettingsArr[0]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 1, classes)}>
                {SCISettingsArr[1]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 2, classes)}>
                {SCISettingsArr[2]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 3, classes)}>
                {SCISettingsArr[3]}
              </MenuItem>
              <MenuItem onClick={getSCIProperties}
                className={getClass(sciTabValue, 4, classes)}>
                {SCISettingsArr[4]}
              </MenuItem>
            </MenuList>
          </Paper>
        </div>
        <div className={classes.rightContainer}>
          {sciTabValue === 0 && (
            customTabFields ? (
              <SCICustomTab deviceModel={deviceModel}
                customTabFields={customTabFields}
                classes={classes}
                handleCustomSwitchChange={handleCustomSwitchChange}
                noUpdate={noUpdate}
                sciPendingSettingData={sciPendingSettingData}
                getIsActive={getIsActive}/>
            ) : (<NoData classes={classes} />)
          )}
          {sciTabValue === 1 && (
            reportTabFields ? (
              <SCIReportsTab reportTabFields={reportTabFields}
                classes={classes}
                handleReportAndLogs={handleReportAndLogs}
                noUpdate={noUpdate}
                sciPendingSettingData={sciPendingSettingData}
                getIsActive={getIsActive}
                initialReportAndLog={initialReportAndLog}/>
            ) : (<NoData classes={classes} />)
          )}
          {sciTabValue === 2 && (
            sensorsTabFields ? (
              <SCISensorsTab deviceModel={deviceModel}
                sensorsTabFields={sensorsTabFields}
                classes={classes}
                handleSensorsFieldChange={handleSensorsFieldChange}
                noUpdate={noUpdate}
                sciPendingSettingData={sciPendingSettingData}
                getIsActive={getIsActive}
                pendingSensorValueCheck={pendingSensorValueCheck}/>
            ) : (<NoData classes={classes} />)
          )}
          {sciTabValue === 3 && (
            <SCIWifiTab wifiNetworks={wifiNetworks}
              classes={classes}
              handleNetworkChange={handleNetworkChange}
              noUpdate={noUpdate}
              onAddWifiNetwork={onAddWifiNetwork}
              onRemoveWifiNetwork={onRemoveWifiNetwork}
              sciPendingSettingData={sciPendingSettingData}
              newWifiNetwork={newWifiNetwork}/>
          )}
          {sciTabValue === 4 && (
            advanceTabFields ? (
              renderAdvance()
            ) : (<NoData classes={classes} />)
          )}
        </div>
      </div>
      <div className={classes.btnStyle}>
        <PrimaryButton onClick={onSCISubmit}>
          {
            isSciLoading ? (
              <ButtonSpinner />
            ) : <>{translate('common:editors.shared.done')}</>
          }
        </PrimaryButton>
      </div>
    </div>
  );
};

export default SCISettingsTab;
